<template>
  <div>
    <section class="container-fluid my-4">
      <div class="d-flex justify-content-between align-items-center my-4">
        <h4 class="mb-0">Relação de <strong>Candidatos</strong></h4>
        <div>
          <button type="button" class="btn btn-outline-success btn-sm me-2" @click="exportExcel" :disabled="exporting">
            Exportar Excel
          </button>
          <router-link :to="{ name: 'candidate-register' }" class="btn btn-success btn-sm text-white">
            Novo candidato
          </router-link>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-4 mb-2">
          <input
            type="text"
            class="form-control"
            placeholder="Buscar por nome ou telefone"
            v-model="search"
          />
        </div>
        <div class="col-md-2 mb-2">
          <input type="date" class="form-control" v-model="start_date" @change="getItens(1)" />
        </div>
        <div class="col-md-2 mb-2">
          <input type="date" class="form-control" v-model="end_date" @change="getItens(1)" />
        </div>
        <div class="col-md-2 mb-2">
          <SearchableSelect
            :value="course_id"
            :options="courses"
            item-text="name"
            item-value="id"
            placeholder="Todos os cursos"
            @input="onCursoChange"
          />
        </div>
        <div class="col-md-2 mb-2">
          <select class="form-control" v-model="status" @change="getItens(1)">
            <option value="">Todos os status</option>
            <option v-for="option in candidateStatusOptions" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-3 mb-2" v-if="isAdmin">
          <SearchableSelect
            :value="user_id"
            :options="users"
            item-text="name"
            item-value="id"
            placeholder="Todos os atendentes"
            @input="onAtendenteChange"
          />
        </div>
        <div class="col-md-3 mb-2">
          <select class="form-control" v-model="branch_id" @change="getItens(1)">
            <option value="">Todas as filiais</option>
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">
              {{ branch.name }}
            </option>
          </select>
        </div>
        <div class="col-md-3 mb-2">
          <input
            type="text"
            class="form-control"
            placeholder="Filtrar por cidade"
            v-model="city"
            @change="getItens(1)"
          />
        </div>
        <div class="col-md-2 mb-2">
          <select class="form-control" v-model="state" @change="getItens(1)">
            <option value="">Todas as UFs</option>
            <option v-for="uf in brazilianStates" :key="uf.id" :value="uf.id">
              {{ uf.name }}
            </option>
          </select>
        </div>
      </div>

      <div
        class="dashboard-strip mb-4"
        v-if="summary || loadingSummary"
        :class="{ 'dash-loading': loadingSummary }"
        :aria-busy="loadingSummary ? 'true' : 'false'"
      >
        <div class="dash-status" v-if="loadingSummary" role="status">
          <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          <span>Carregando indicadores...</span>
        </div>

        <template v-if="summary">
        <div class="dash-card">
          <div class="dash-title">Por status</div>
          <div class="dash-item" v-for="item in summary.by_status" :key="item.status">
            <span>{{ item.status }}</span><strong>{{ item.total }}</strong>
          </div>
        </div>

        <div class="dash-card" v-if="isAdmin">
          <div class="dash-title">Top 3 atendentes</div>
          <div class="dash-item" v-for="item in summary.top_users" :key="item.id">
            <span>{{ item.name }}</span><strong>{{ item.total }}</strong>
          </div>
        </div>

        <div class="dash-card">
          <div class="dash-title">Top 3 UF</div>
          <div class="dash-item" v-for="item in summary.top_states" :key="item.state">
            <span>{{ item.state }}</span><strong>{{ item.total }}</strong>
          </div>
        </div>

        <div class="dash-card">
          <div class="dash-title">Última comissão</div>
          <div class="dash-value">{{ summary.last_commission ? summary.last_commission.percent + "%" : "—" }}</div>
          <div class="dash-sub" v-if="summary.last_commission">{{ summary.last_commission.candidate }}</div>
        </div>

        <div class="dash-card">
          <div class="dash-title">Top 3 formas de pagamento</div>
          <div class="dash-item" v-for="item in summary.top_payment_types" :key="item.id">
            <span>{{ item.name }}</span><strong>{{ item.total }}</strong>
          </div>
        </div>
        <div class="dash-card">
          <div class="dash-title">Últimos 3 fechamentos (valor)</div>
          <div class="dash-item" v-for="(item, index) in summary.last_closings" :key="index">
            <span>{{ item.candidate }}</span><strong>{{ formatCurrency(item.value) }}</strong>
          </div>
        </div>
        <div class="dash-card">
          <div class="dash-title">Taxa de conversão</div>
          <div class="dash-value">{{ summary.conversion.percent }}%</div>
          <div class="dash-sub">{{ summary.conversion.enrolled }} de {{ summary.conversion.total }} matriculados</div>
        </div>

        <div class="dash-card">
          <div class="dash-title">Comissão a repassar</div>
          <div class="dash-value">{{ formatCurrency(summary.total_commission) }}</div>
          <div class="dash-sub">Sobre o valor pago</div>
        </div>
        </template>
      </div>

      <table class="table table-bordered table-striped table-hover">
        <thead>
          <tr>
            <th><a href="#" @click="sort($event, 'name')">Nome</a></th>
            <th>Telefone</th>
            <th><a href="#" @click="sort($event, 'city')">Cidade/UF</a></th>
            <th>Curso</th>
            <th><a href="#" @click="sort($event, 'created_at')">Data</a></th>
            <th>Status</th>
            <th>Filial</th>
            <th v-if="isAdmin">Atendente</th>
            <th width="98px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td>{{ item.name }}</td>
            <td>
              <button
                v-if="item.phone"
                type="button"
                class="btn btn-sm btn-success"
                @click="openWhatsApp(item.phone)"
                title="Abrir conversa no WhatsApp"
              >
                {{ item.phone }}
              </button>
            </td>
            <td>{{ item.state ? item.city + "/" + item.state : item.city }}</td>
            <td>{{ item.course ? item.course.name : "" }}</td>
            <td>{{ item.created_at }}</td>
            <td>
              <select class="form-control form-control-sm" v-model="item.status" @change="updateStatus(item)">
                <option v-for="option in candidateStatusOptions" :key="option.id" :value="option.id">
                  {{ option.name }}
                </option>
              </select>
            </td>
            <td>{{ item.branch ? item.branch.name : "" }}</td>
            <td v-if="isAdmin">{{ item.user ? item.user.name : "" }}</td>
            <td>
              <ButtonEdit :to="{ name: 'candidate-edit', params: { id: item.id } }" />
              <ButtonDelete @delete="handlerDelete(item.id, 'candidates')" />
            </td>
          </tr>
          <tr v-if="!items.length">
            <td :colspan="isAdmin ? 9 : 8" class="text-center text-muted">Nenhum candidato encontrado.</td>
          </tr>
        </tbody>
      </table>

      <Pagination :source="pagination" @navigate="navigate" />
    </section>
  </div>
</template>

<script>
import api from "@/services";
import Pagination from "@/components/Pagination.vue";
import ButtonEdit from "@/components/ButtonEdit.vue";
import ButtonDelete from "@/components/ButtonDelete.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import { serialize, handlerDelete, candidateStatusOptions, brazilianStates, handleBlobError } from "@/helpers";

export default {
  name: "CandidateList",
  components: {
    Pagination,
    ButtonEdit,
    ButtonDelete,
    SearchableSelect,
  },
  data() {
    return {
      candidateStatusOptions,
      brazilianStates,
      sortProperty: "created_at",
      sortDirection: 1,
      search: "",
      start_date: "",
      end_date: "",
      course_id: "",
      status: "",
      pagination: {},
      items: [],
      courses: [],
      users: [],
      branches: [],
      user_id: "",
      branch_id: "",
      city: "",
      state: "",
      isAdmin: false,
      exporting: false,
      handlerDelete,
      currentPage: 1,
      summary: null,
      loadingSummary: false,
      summaryRequestId: 0,
    };
  },
  methods: {
    openWhatsApp(phone) {
      if (!phone) {
        Toast.fire("Aviso", "Nenhum telefone cadastrado", "warning");
        return;
      }

      const phoneNumber = phone.replace(/\D/g, "");

      if (phoneNumber.length < 10) {
        Toast.fire("Aviso", "Número de telefone inválido", "warning");
        return;
      }

      const fullNumber = phoneNumber.startsWith("55") ? phoneNumber : `55${phoneNumber}`;
      const message = encodeURIComponent("Saudações! Aqui é da equipe da Paideia Educacional");
      const whatsappUrl = `https://web.whatsapp.com/send?phone=${fullNumber}&text=${message}`;

      window.open(whatsappUrl, "_blank");
    },
    filters() {
      const query = {
        sortDesc: this.sortDirection,
        sortBy: this.sortProperty,
      };

      if (this.search) query.search = this.search;
      if (this.start_date) query.start_date = this.start_date;
      if (this.end_date) query.end_date = this.end_date;
      if (this.course_id) query.course_id = this.course_id;
      if (this.status) query.status = this.status;
      if (this.user_id) query.user_id = this.user_id;
      if (this.branch_id) query.branch_id = this.branch_id;
      if (this.city) query.city = this.city;
      if (this.state) query.state = this.state;

      return query;
    },
    sort(ev, property) {
      ev.preventDefault();
      this.sortProperty = property;
      this.sortDirection = this.sortDirection ? 0 : 1;
      this.getItens();
    },
    navigate(page) {
      this.getItens(page);
    },
    async getCourses() {
      const { data } = await api.get("/courses?page=1&perPage=99999");
      this.courses = [...data.data].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    },
    onCursoChange(value) {
      this.course_id = value;
      this.getItens(1);
    },
    onAtendenteChange(value) {
      this.user_id = value;
      this.getItens(1);
    },
    async getUsers() {
      const { data } = await api.get("/users?page=0&perPage=99999");
      this.users = [...data.data].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    },
    async getBranches() {
      const { data } = await api.get("/branches?perPage=99999");
      this.branches = data.data;
    },
    async getItens(page = 1) {
      this.currentPage = page;
      const query = serialize({
        ...this.filters(),
        perPage: 20,
      });

      const { data } = await api.get(`/candidates?page=${page}` + query);
      this.items = data.data;
      this.pagination = data;
      this.getSummary();
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    async getSummary() {
      const requestId = ++this.summaryRequestId;
      this.loadingSummary = true;

      try {
        const { data } = await api.get("/candidates/summary", { params: this.filters() });
        if (requestId === this.summaryRequestId) this.summary = data;
      } catch (error) {
        if (requestId === this.summaryRequestId) this.summary = null;
      } finally {
        if (requestId === this.summaryRequestId) this.loadingSummary = false;
      }
    },
    async updateStatus(item) {
      try {
        const { data } = await api.put(`/candidates/${item.id}/status`, { status: item.status });
        Toast.fire(data.message, "", "success");
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message;
        Toast.fire(message || "Não foi possível atualizar o status.", "", "error");
        this.getItens(this.currentPage);
      }
    },
    async exportExcel() {
      this.exporting = true;

      try {
        const response = await api.get("/candidates/export", {
          params: this.filters(),
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
        link.setAttribute("download", `candidatos_${stamp}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        handleBlobError(error, (message) => {
          Toast.fire("Erro", message, "error");
        });
      } finally {
        this.exporting = false;
      }
    },
  },
  async mounted() {
    try {
      const { data } = await api.post("/auth/me");
      this.isAdmin = data.role === "admin";
      if (data.role) window.localStorage.role = data.role;
    } catch (error) {
      this.isAdmin = window.localStorage.role === "admin";
    }

    this.getCourses();
    this.getBranches();
    if (this.isAdmin) this.getUsers();
    this.getItens();
  },
  watch: {
    search() {
      this.getItens();
    },
  },
};
</script>

<style scoped>
.dashboard-strip {
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.dash-card {
  flex: 0 0 200px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.dash-title {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.dash-item {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.875rem;
  padding: 0.15rem 0;
  font-variant-numeric: tabular-nums;
}

.dash-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-value {
  font-size: 1.5rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.dash-status {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
  padding: 0 0.5rem;
}

.dash-loading .dash-card {
  opacity: 0.5;
  transition: opacity 0.2s ease;
}

.dash-sub {
  font-size: 0.8rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
